import { OrganizationalContainer } from '../models/organizational-container.interface';
import { Event } from '@trustedshops/tswp-core-common';
import { RendererInfo } from '../models/renderer-info.interface';

/**
 * Selection backend for the organizational container selection control.
 * Manages current selection and rendering information.
 */
export interface OrganizationalContainerSelectionService {
  //#region Properties
  /**
   * Gets the current HTML element name rendering an organizational container
   */
  readonly currentContainerRenderer: Event<RendererInfo>;

  /**
   * Gets the default organizational container renderer
   */
  readonly defaultContainerRenderer: RendererInfo;

  /**
   * Changes the rendering instance of all organizational containers
   *
   * @param rendererInfo Info about web component name and possibly related plugin to load
   */
  changeContainerRenderer(rendererInfo: RendererInfo): void;

  /**
   * Sets the organizational containers to be selected and notifies subsribers listening to this list
   *
   * @param selectedContainers The containers to select
   * @returns A promise resolving when the new selection has been emitted.
   */
  selectContainers<T>(
    selectedContainers: OrganizationalContainer<T>[]
  ): Promise<void>;

  /**
   * Gets an event stream notifiying about changes in the selection of organizational containers
   */
  getSelection<T>(): Event<OrganizationalContainer<T>[]>;

  /**
   * Gets all organizational containers available for selection
   */
  getAllContainers<T>(): Event<OrganizationalContainer<T>[]>;

  /**
   * Sets the action permissions required to see a container, e.g. to
   * express a route-level requirement (the carrier shell calls this on
   * every navigation).
   *
   * This caller's contribution participates in the same aggregate as every
   * {@link registerRequiredPermissions} owner — see that method's doc for
   * the aggregation semantics (currently: union/OR-of-ANDs across every
   * caller, including this one). Calling this again replaces only *this*
   * caller's own contribution to the aggregate, not any registered owner's.
   */
  setRequiredPermissions(...actions: string[][]): void | Promise<void>;

  /**
   * Registers one caller's required-permissions stream under a stable
   * `ownerId`. The service aggregates every currently-registered owner's
   * most recent value — together with {@link setRequiredPermissions}'s own
   * latest call, if any — into the actual permission filter used to decide
   * which containers are selectable. Independent callers (e.g. several
   * widgets composed on one page) no longer need to coordinate with each
   * other or wait for one another before calling
   * {@link setRequiredPermissions} once with a merged result.
   *
   * **Aggregation is currently a union (OR-of-ANDs, concatenation) across
   * every caller, deliberately** — appropriate for independent sibling
   * widgets on one page (any of them rendering something meaningful is
   * enough to keep a container selectable), and it's also what merging
   * page-level ref-based aggregation into this API preserves. It has *not*
   * been evaluated for narrowing use cases — e.g. a route wanting only
   * `channel`-type containers (via {@link setRequiredPermissions}) while a
   * widget on that route separately requires `shop:read`: today the result
   * is "either", not "both restricted to channels AND shop:read". If a
   * narrowing/intersection use case shows up, it needs a real design
   * decision (e.g. a separate parameter, or per-owner AND-vs-OR opt-in),
   * not a silent behavior change here.
   *
   * Registering again under the same `ownerId` replaces only that owner's
   * prior contribution, not anyone else's.
   *
   * @param ownerId Stable identifier for this contribution (e.g. a plugin
   * or custom-element tag name). Must be unique per independent caller.
   * @param permissions Stream of this caller's required action sets. Every
   * emission recomputes and re-applies the aggregate across all owners.
   * @returns A function that unregisters this owner's contribution
   * (unsubscribes and removes it from the aggregate) — call on cleanup
   * (e.g. component unmount).
   */
  registerRequiredPermissions(
    ownerId: string,
    permissions: Event<string[][]>
  ): () => void;

  /**
   * Requests to open the organizational container selector's list and show it to the User in the UI.
   */
  showSelectorUI(): void;
}
